import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import axios from 'axios';
import * as authService from '../services/auth';
import type { User } from '../services/auth';
import { syncPushSubscriptionWithServer } from '../services/notifications';

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, name: string) => Promise<void>;
  logout: () => void;
  updateProfile: (input: { name?: string; bio?: string; profileImageUrl?: string; phone?: string; contactEmail?: string }) => Promise<void>;
  refreshUser: () => Promise<void>;
  makeFirstUserAdmin: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load user from localStorage on mount, then refresh from backend
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        // Ensure all required fields exist (for backwards compatibility)
        const completeUser: User = {
          ...parsedUser,
          isAdmin: parsedUser.isAdmin ?? false,
          mustChangePassword: parsedUser.mustChangePassword ?? false,
        };
        setToken(storedToken);
        setUser(completeUser);
      } catch (e) {
        // Invalid user data in localStorage
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setIsLoading(false);
        return;
      }
      setIsLoading(false);
      // Refresh from backend to pick up fields not in old cached object (phone, contactEmail, isAdmin)
      authService.getProfile()
        .then((freshUser) => {
          setUser(freshUser);
          localStorage.setItem('user', JSON.stringify(freshUser));
          // Silently re-sync push subscription so the server has a valid record
          // even if it was deleted after a previous 410 expiry response.
          syncPushSubscriptionWithServer();
        })
        .catch((err) => {
          // Only log out on a genuine auth failure (invalid/expired token).
          // A network error or timeout here (e.g. flaky mobile connection on
          // app start) must not wipe out an otherwise valid saved session —
          // that was forcing users to log in twice.
          const status = axios.isAxiosError(err) ? err.response?.status : undefined;
          if (status === 401 || status === 403) {
            setToken(null);
            setUser(null);
            authService.logout();
          }
        });
    } else {
      setIsLoading(false);
    }
  }, []);

  const login = async (email: string, password: string) => {
    const response = await authService.login({ email, password });
    setToken(response.token);
    setUser(response.user);
    localStorage.setItem('token', response.token);
    localStorage.setItem('user', JSON.stringify(response.user));
  };

  const signup = async (email: string, password: string, name: string) => {
    const response = await authService.signup({ email, password, name });
    setToken(response.token);
    setUser(response.user);
    localStorage.setItem('token', response.token);
    localStorage.setItem('user', JSON.stringify(response.user));
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  const updateProfile = async (input: { name?: string; bio?: string; profileImageUrl?: string }) => {
    const updatedUser = await authService.updateProfile(input);
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const refreshUser = async () => {
    try {
      const freshUser = await authService.getProfile();
      setUser(freshUser);
      localStorage.setItem('user', JSON.stringify(freshUser));
    } catch {
      // ignore
    }
  };

  const makeFirstUserAdmin = async () => {
    try {
      const result = await authService.makeFirstUserAdmin();
      // Update local user object with new admin status
      setUser(result.user);
      localStorage.setItem('user', JSON.stringify(result.user));
    } catch (error) {
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!token && !!user,
    isLoading,
    login,
    signup,
    logout,
    updateProfile,
    refreshUser,
    makeFirstUserAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
